import React, { useState, useEffect, useCallback } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  Platform,
  TouchableOpacity,
  SafeAreaView,
} from "react-native";
import { router } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { ThemedButton } from "@/components/ThemedButton";
import { CustomAlert } from "@/components/ThemedAlert";
import { FeedbackModal } from "@/components/feedback/FeedbackModal";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors, Fonts, Spacing } from "@/constants/ThemeColours";
import { logAndFlush, flushQueue } from "@/services/batch-logger";
import {
  collectFeedbackDiagnostics,
  formatFeedbackDiagnosticsMarkdown,
} from "@/services/feedback-metadata";
import { useAuth } from "@/components/auth/useAuth";

/**
 * Props for the standard Memories ErrorScreen component.
 */
export interface IErrorScreenProps {
  /** The error object caught by the runtime or error boundary */
  error?: Error | null;
  /** Component stack trace or contextual error info */
  errorInfo?: { componentStack?: string | null } | null;
  /** Callback to reset or retry the failed component */
  onRetry?: () => void;
  /** Optional callback to navigate back to the previous screen */
  onGoBack?: () => void;
  /** Optional custom title for the error presentation */
  title?: string;
  /** Optional custom description */
  description?: string;
}

/**
 * Standard Error screen adhering to Memories design tokens and UX standards.
 * Features immediate server log flushing, expandable technical diagnostics,
 * feedback reporting, and safe screen recovery.
 *
 * @param {IErrorScreenProps} props - Component properties.
 * @returns {React.JSX.Element} The rendered error screen.
 */
export function ErrorScreen({
  error,
  errorInfo,
  onRetry,
  onGoBack,
  title = "Something Went Unexpectedly Wrong",
  description = "We encountered an unexpected issue while displaying this screen. An automatic error diagnostic report has been captured and flushed to the server.",
}: IErrorScreenProps): React.JSX.Element {
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [feedbackModalVisible, setFeedbackModalVisible] = useState<boolean>(false);
  const [isReporting, setIsReporting] = useState<boolean>(false);
  const [reportSuccess, setReportSuccess] = useState<boolean>(false);

  const { user } = useAuth();

  const backgroundColor = useThemeColor({}, "background");
  const cardColor = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "text");
  const subtextColor = useThemeColor({}, "tabIconDefault");
  const codeBg = useThemeColor({}, "inputReadOnly");
  const codeBorder = useThemeColor({}, "inputReadOnlyBorder");

  // Ensure remote log pipeline has flushed any pending logs to Google Cloud Logging
  useEffect(() => {
    flushQueue().catch((flushErr) => {
      console.warn("[ErrorScreen] Background flush queue error:", flushErr);
    });
  }, []);

  /**
   * Retries rendering by calling the provided retry callback.
   */
  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
    }
  }, [onRetry]);

  /**
   * Navigates back to the previous screen or safely redirects to home.
   */
  const handleGoBack = useCallback(() => {
    if (onGoBack) {
      onGoBack();
      return;
    }

    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/(tabs)" as any);
    }

    // Reset error state so subsequent navigations are not blocked
    if (onRetry) {
      onRetry();
    }
  }, [onGoBack, onRetry]);

  /**
   * Explicitly submits an immediate error report to remote logs with full diagnostics.
   */
  const handleReportError = useCallback(async () => {
    setIsReporting(true);
    try {
      const diagnostics = collectFeedbackDiagnostics(user);
      const diagnosticsMd = formatFeedbackDiagnosticsMarkdown(diagnostics);

      const errorMessage = error?.message || String(error) || "Unknown error";
      const errorStack = error?.stack || "No stack trace available";
      const compStack = errorInfo?.componentStack || "No component stack available";

      const logPayload = [
        `[USER_REPORTED_ERROR] ${errorMessage}`,
        `Stack: ${errorStack}`,
        `ComponentStack: ${compStack}`,
        `\nDiagnostics:\n${diagnosticsMd}`,
      ].join("\n");

      logAndFlush("error", `[UserErrorReport] ${errorMessage}`, logPayload);
      await flushQueue();

      setReportSuccess(true);
      CustomAlert.alert(
        "Error Reported",
        "Thank you. Detailed diagnostic information has been flushed and sent directly to our servers."
      );
    } catch (err: any) {
      console.error("[ErrorScreen] Failed to report error:", err);
      CustomAlert.alert(
        "Report Failed",
        err?.message || "Could not flush error logs at this moment. Please try again later."
      );
    } finally {
      setIsReporting(false);
    }
  }, [error, errorInfo, user]);

  /**
   * Opens the integrated Memories feedback modal with prefilled error context.
   */
  const handleOpenFeedback = useCallback(() => {
    setFeedbackModalVisible(true);
  }, []);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor }]}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.card, { backgroundColor: cardColor, borderColor }]}>
          {/* Warning Icon Container */}
          <View style={styles.iconCircle}>
            <Ionicons
              name="alert-circle-outline"
              size={48}
              color={BrandColors.sungoldOrange.hex}
            />
          </View>

          {/* Heading and Summary */}
          <ThemedText type="editorialTitle" style={styles.title}>
            {title}
          </ThemedText>

          <ThemedText style={[styles.description, { color: subtextColor }]}>
            {description}
          </ThemedText>

          {/* Collapsible Technical Details */}
          <View style={styles.accordionContainer}>
            <TouchableOpacity
              onPress={() => setShowDetails((prev) => !prev)}
              style={styles.detailsToggle}
              accessibilityRole="button"
              accessibilityLabel={showDetails ? "Hide technical details" : "View technical details"}
            >
              <Ionicons
                name={showDetails ? "chevron-up" : "chevron-down"}
                size={16}
                color={textColor}
              />
              <ThemedText type="smallBold" style={[styles.detailsToggleText, { color: textColor }]}>
                {showDetails ? "Hide Technical Details" : "View Technical Details"}
              </ThemedText>
            </TouchableOpacity>

            {showDetails && (
              <View
                style={[
                  styles.codeBox,
                  { backgroundColor: codeBg, borderColor: codeBorder },
                ]}
              >
                <ThemedText
                  type="smallBold"
                  style={[styles.errorMessageText, { color: BrandColors.sungoldOrange.hex }]}
                >
                  {error?.name || "Error"}: {error?.message || "An unexpected runtime error occurred."}
                </ThemedText>

                {error?.stack ? (
                  <ScrollView
                    style={styles.stackScrollView}
                    nestedScrollEnabled
                    horizontal
                  >
                    <ThemedText type="code" style={styles.codeText} selectable>
                      {error.stack}
                    </ThemedText>
                  </ScrollView>
                ) : null}

                {errorInfo?.componentStack ? (
                  <ScrollView
                    style={styles.stackScrollView}
                    nestedScrollEnabled
                    horizontal
                  >
                    <ThemedText type="code" style={styles.codeText} selectable>
                      {`Component Stack:\n${errorInfo.componentStack}`}
                    </ThemedText>
                  </ScrollView>
                ) : null}
              </View>
            )}
          </View>

          {/* Action Buttons */}
          <View style={styles.actionsContainer}>
            <ThemedButton
              title="Try Again"
              variant="primary"
              icon={<Ionicons name="refresh-outline" size={18} color="#FFFFFF" />}
              onPress={handleRetry}
              style={styles.actionButton}
              accessibilityLabel="Try again"
            />

            <ThemedButton
              title="Provide Feedback & Diagnostics"
              variant="secondary"
              icon={
                <Ionicons
                  name="chatbubble-ellipses-outline"
                  size={18}
                  color={textColor}
                />
              }
              onPress={handleOpenFeedback}
              style={styles.actionButton}
              accessibilityLabel="Provide feedback and diagnostics"
            />

            <ThemedButton
              title={reportSuccess ? "Error Report Sent ✓" : "Report This Error"}
              variant="secondary"
              icon={
                <Ionicons
                  name={reportSuccess ? "checkmark-circle-outline" : "bug-outline"}
                  size={18}
                  color={textColor}
                />
              }
              onPress={handleReportError}
              loading={isReporting}
              disabled={reportSuccess}
              style={styles.actionButton}
              accessibilityLabel="Report this error to logs"
            />

            <ThemedButton
              title="Go Back to Previous Screen"
              variant="minimal"
              icon={<Ionicons name="arrow-back-outline" size={18} color={textColor} />}
              onPress={handleGoBack}
              style={styles.actionButton}
              accessibilityLabel="Go back to previous screen"
            />
          </View>
        </View>
      </ScrollView>

      {/* Integrated Feedback Modal with Error Context */}
      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={() => setFeedbackModalVisible(false)}
        initialDescription={error?.message ? `I encountered an error: ${error.message}` : "I encountered an error on this screen."}
        errorContext={{
          message: error?.message,
          stack: error?.stack || errorInfo?.componentStack || undefined,
        }}
      />
    </SafeAreaView>
  );
}

export default ErrorScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: Spacing.four,
  },
  card: {
    width: "100%",
    maxWidth: 520,
    borderRadius: 16,
    padding: Spacing.four,
    alignItems: "center",
    borderWidth: 1,
    ...Platform.select({
      android: {
        elevation: 4,
      },
      default: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 14,
      },
    }),
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: Spacing.three,
    backgroundColor: "rgba(234, 88, 12, 0.12)",
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    textAlign: "center",
    marginBottom: Spacing.two,
  },
  description: {
    fontFamily: Fonts.sans,
    fontSize: 14,
    lineHeight: 21,
    textAlign: "center",
    marginBottom: Spacing.four,
  },
  accordionContainer: {
    width: "100%",
    marginBottom: Spacing.four,
  },
  detailsToggle: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: Spacing.one,
    paddingVertical: Spacing.two,
    minHeight: 44,
  },
  detailsToggleText: {
    fontSize: 13,
  },
  codeBox: {
    marginTop: Spacing.two,
    padding: Spacing.three,
    borderRadius: 8,
    borderWidth: 1,
    width: "100%",
  },
  errorMessageText: {
    marginBottom: Spacing.two,
    fontSize: 12,
  },
  stackScrollView: {
    maxHeight: 140,
    marginTop: Spacing.one,
  },
  codeText: {
    fontSize: 11,
    lineHeight: 16,
  },
  actionsContainer: {
    width: "100%",
    gap: Spacing.two + Spacing.one,
  },
  actionButton: {
    width: "100%",
    minHeight: 48,
  },
});
